/**
 * ComplianceAlert — Non-HR UX layer (HUMANAI_UX_NONHR.md §3, หลักการ 4).
 * Ported from PayPilot apps/liff (teal accent → eval brand/violet).
 *
 * "เตือนก่อนพลาด ไม่ใช่ปล่อยผิดแล้วโดนปรับ" — component กลางสำหรับเตือนเชิงรุก
 * เมื่อ action เสี่ยงผิดกฎหมาย/นโยบาย. ระบบรู้กฎแทนผู้ใช้ที่ไม่ใช่ HR.
 *
 * 3 ระดับ: block (ผิดกฎหมายแน่/ทำต่อไม่ได้) · warn (เสี่ยง ทำต่อได้แต่ต้องรู้ตัว) · info (ข้อควรรู้)
 * Portable: card + lucide + semantic color เท่านั้น.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type ComplianceLevel = 'block' | 'warn' | 'info';

export interface ComplianceItem {
  level: ComplianceLevel;
  /** หัวข้อสั้น ภาษาคน — ไม่ใช่ jargon */
  title: string;
  /** คำอธิบายเพิ่ม — บอกว่าจะเกิดอะไร / ต้องทำอะไร */
  detail?: ReactNode;
  /** ที่มา/อ้างอิงกฎหมาย — สร้างความน่าเชื่อถือ */
  cite?: string;
  /** จำนวนรายการที่เข้าเงื่อนไข */
  count?: number;
  /** ปุ่ม/ลิงก์ไปแก้ที่ต้นเหตุ */
  action?: { label: string; to?: string; onClick?: () => void };
}

const LEVEL_STYLE: Record<
  ComplianceLevel,
  { border: string; icon: string; Icon: typeof AlertCircle; chip: string }
> = {
  block: { border: 'border-l-red-600', icon: 'text-red-600', Icon: AlertCircle, chip: 'bg-red-100 text-red-700' },
  warn: { border: 'border-l-amber-400', icon: 'text-amber-600', Icon: AlertTriangle, chip: 'bg-amber-100 text-amber-700' },
  info: { border: 'border-l-sky-400', icon: 'text-sky-600', Icon: Info, chip: 'bg-blue-100 text-blue-700' },
};

/** เตือนเดี่ยว 1 รายการ */
export function ComplianceAlert({ level, title, detail, cite, count, action }: ComplianceItem) {
  const s = LEVEL_STYLE[level];
  const { Icon } = s;
  return (
    <div className={`card border-l-4 ${s.border} px-3 py-3 flex gap-3`}>
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${s.icon}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          {typeof count === 'number' && <span className={`badge ${s.chip} shrink-0 tabular-nums`}>{count}</span>}
        </div>
        {detail && <p className="text-sm text-gray-600 mt-0.5">{detail}</p>}
        {cite && <p className="text-xs text-gray-400 mt-1">อ้างอิง: {cite}</p>}
        {action &&
          (action.to ? (
            <Link to={action.to} className="inline-block text-sm font-medium text-brand-600 mt-1.5 hover:underline">
              {action.label} →
            </Link>
          ) : (
            <button type="button" onClick={action.onClick} className="text-sm font-medium text-brand-600 mt-1.5 hover:underline">
              {action.label} →
            </button>
          ))}
      </div>
    </div>
  );
}

/** รายการเตือนหลายรายการ จัดกลุ่มตามระดับ (block → warn → info). items ว่าง → null. */
export function ComplianceAlertList({ items, summary }: { items: ComplianceItem[]; summary?: ReactNode }) {
  if (!items.length) return null;
  const order: ComplianceLevel[] = ['block', 'warn', 'info'];
  const sorted = [...items].sort((a, b) => order.indexOf(a.level) - order.indexOf(b.level));
  return (
    <div className="space-y-2">
      {summary}
      {sorted.map((it, i) => (
        <ComplianceAlert key={`${it.level}-${it.title}-${i}`} {...it} />
      ))}
    </div>
  );
}
